import classNames from 'classnames'
import {
  Button,
  ProductCard,
  Tile,
  useMediaQuery,
  Image,
  Link,
  Skeleton,
} from '@lounge-fe/ui-kit'
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import {
  MemoisedProductsProps,
  PromoCardLayout,
  VirtualizedProductCardProps,
} from './types'
import { StandardProductCard } from '@/features/product/components/StandardProductCard'
import { Price } from '@/features/product/components/StandardProductCard/Price'
import { useEntryPointContext } from '@/components'
import { CollectionPageProps } from '../types'
import { useProductGridResized } from '../useProductGridResized'
import { StandardProductCardRef } from '@/features/product/components/StandardProductCard/types'
import { twMerge } from 'tailwind-merge'
import { useProductBadges } from '@/features/product/components/StandardProductCard/hooks/useProductBadges'

const promoCardLayoutClasses: { [key in PromoCardLayout]: string } = {
  '1x1': 'col-span-1 sm:row-span-1 aspect-[2/3]',
  '2x1': 'col-span-2 sm:col-span-2 sm:row-span-1',
}

const MIN_QUICK_ADD_CONTAINER_WIDTH = 600

const VirtualizedProductCard = ({
  product,
  handle,
  onClick,
  onQuickAdd,
  settings,
  gridSize,
  idx,
}: VirtualizedProductCardProps) => {
  const productCardRef = useRef<StandardProductCardRef | null>(null)
  const { data } = useEntryPointContext<CollectionPageProps>()
  const { subscribe: subscribeGridResized } = useProductGridResized()
  const [showQuickAdd, setShowQuickAdd] = useState(true)
  const isDesktop = useMediaQuery((theme) => `(min-width: ${theme.screens.xl})`)

  const { primaryBadge, secondaryBadge } = useProductBadges({
    badges: product.badges,
    price: product.price,
    price_compare_at: product.price_compare_at,
    tags: product.tags,
  })

  const { ref, inView } = useInView({
    rootMargin: '0px 0px 350px 0px',
  })

  useEffect(() => {
    if (inView) {
      const unsubscribe = subscribeGridResized(([entry]) => {
        productCardRef.current?.imageCarousel?.reInit()
        const { width } = entry.contentRect
        setShowQuickAdd(width > MIN_QUICK_ADD_CONTAINER_WIDTH)
      })

      return () => {
        setShowQuickAdd(true)
        unsubscribe()
      }
    }

    return () => {
      setShowQuickAdd(true)
    }
  }, [inView])

  if (gridSize === 'sm') {
    if (product.images.length == 0) {
      return null
    }
    return (
      <div className="collection__product-card" ref={ref} data-inview={inView}>
        {inView && product.images.length && product.images[0].src ? (
          <div className="relative overflow-hidden bg-surface-primary-default">
            <a
              href={
                handle ? `/collections/${handle}${product.href}` : product.href
              }
              onClick={() => onClick(product)}
            >
              <Image
                data={{
                  src: product.images[0].src,
                  width: isDesktop ? 400 : 200,
                  height: isDesktop ? 400 : 200,
                }}
                loading={idx <= 12 ? 'eager' : 'lazy'}
                className="object-cover w-full h-full aspect-[2/3]"
              />
            </a>
          </div>
        ) : (
          <Skeleton key={idx}>
            <Skeleton.Block className="w-full bg-surface-primary-default aspect-[2/3]" />
          </Skeleton>
        )}
      </div>
    )
  }

  return (
    <div className="collection__product-card" ref={ref} data-inview={inView}>
      {inView ? (
        <StandardProductCard
          ref={productCardRef}
          key={product.id}
          aria-label={`View ${product.title}`}
          data-testid="product-card"
          product={{
            ...product,
            handle: product.href,
            images: product.images.map(({ src, alt }, imageIdx) => ({
              src,
              alt,
              // Only eager-load first image for first 8 products
              loading: idx <= 8 && imageIdx === 0 ? 'eager' : 'lazy',
            })),
          }}
          onClick={() => onClick(product)}
          onQuickAdd={() => onQuickAdd(product)}
          useSwatches={data?.enable_swatches ?? false}
          useImageCarousel
          useQuickAdd={showQuickAdd || !isDesktop}
        />
      ) : (
        <ProductCard data-testid="product-card" href={product.href}>
          {/* Render basic product details to aid browser search if not in view  */}
          <ProductCard.ImageWrapper>
            <ProductCard.Image alt={product.title} />
          </ProductCard.ImageWrapper>
          <ProductCard.Details
            title={product.title}
            price={<Price price={product.price} />}
            compareAtPrice={
              product.price_compare_at ? (
                <Price price={product.price_compare_at} />
              ) : null
            }
            swatches={data?.enable_swatches ? product.swatches : []}
            primaryBadge={primaryBadge}
            secondaryBadge={secondaryBadge}
            color={product.color}
            isLoadingAdditionalData={product.isLoadingAdditionalData}
          />
        </ProductCard>
      )}
    </div>
  )
}

export const MemoisedProducts = memo(
  ({
    productsOrPromoCards,
    handle,
    settings,
    onClick,
    onQuickAdd,
    gridSize,
  }: MemoisedProductsProps) => {
    const filteredProductsOrPromoCards = useMemo(
      () =>
        gridSize === 'sm'
          ? productsOrPromoCards.filter(
              (productOrPromoCard) => !('layout' in productOrPromoCard),
            )
          : productsOrPromoCards,
      [productsOrPromoCards, gridSize],
    )

    return (
      <>
        {filteredProductsOrPromoCards.map((productOrPromoCard, idx) => {
          if ('layout' in productOrPromoCard) {
            const promoCard = productOrPromoCard
            return (
              <Fragment key={idx}>
                <div
                  className={twMerge(
                    promoCardLayoutClasses[promoCard.layout],
                    gridSize !== 'sm' &&
                      promoCard.layout == '2x1' &&
                      'aspect-[4/3] h-min min-h-[auto] border-b-[3px] border-white',
                    gridSize == 'lg' && 'col-span-1',
                  )}
                >
                  <Tile
                    as={promoCard.href ? 'a' : 'div'}
                    href={promoCard.href}
                    contentPlacement="inside"
                    overlay={promoCard.overlay}
                    className="block"
                  >
                    {promoCard.video ? (
                      <Tile.Video
                        className="h-full"
                        video={promoCard.video}
                        alt={promoCard.title}
                      />
                    ) : (
                      <Tile.Image
                        className="h-full"
                        src={promoCard.image ?? ''}
                        alt={promoCard.title}
                      />
                    )}

                    <Tile.Content
                      className={classNames(
                        gridSize === 'sm' && 'hidden sm:block',
                      )}
                    >
                      {promoCard.subtitle && (
                        <Tile.Subtitle>{promoCard.subtitle}</Tile.Subtitle>
                      )}
                      <Tile.Title>{promoCard.title}</Tile.Title>
                      {promoCard.buttonText && (
                        <Tile.Actions>
                          <Link
                            className="underline underline-offset-4"
                            href={promoCard.href}
                            color={promoCard.buttonVariant}
                          >
                            {promoCard.buttonText}
                          </Link>
                        </Tile.Actions>
                      )}
                    </Tile.Content>
                  </Tile>
                </div>
                <div className="hidden" />
              </Fragment>
            )
          } else {
            const product = productOrPromoCard
            return (
              <VirtualizedProductCard
                idx={idx}
                settings={settings}
                handle={handle}
                onClick={onClick}
                onQuickAdd={onQuickAdd}
                product={product}
                key={product.id}
                gridSize={gridSize}
              />
            )
          }
        })}
      </>
    )
  },
)
